import React from 'react';
import DataProvider from '@app/model/_common/DataProvider';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CompanyHotelService from '@app/model/company_hotel/service/CompanyHotelService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { SiteNotificationQueryKeys } from '@app/model/site_notification/service/SiteNotificationQueryKeys';
import { findProviderData } from '@app/component/provider/FindProviderData';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import Spinner from '@app/component/static/Spinner';
import { replaceCompanyHotelInCache } from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import MenuButtonFilter, { MenuButtonFilterItem } from '@app/component/menu/MenuButtonFilter';
import _isEmpty from 'lodash/isEmpty';


type DashboardSelectProviderFilterProps = {
	filter: MarketStateFilterInput,
	onFilter: (filter: MarketStateFilterInput) => void
}

const DashboardSelectProviderFilter = React.memo(
	function DashboardSelectProviderFilter({ filter, onFilter }: DashboardSelectProviderFilterProps) {
		const { companyHotel } = useSelectedCompanyHotel()!;
		const toaster = useToaster();

		const queryClient = useQueryClient();

		const enableCompanyHotelDataProvider = useMutation({
			mutationFn: CompanyHotelService.enableCompanyHotelDataProvider,
			onSuccess: (data, variables) => {
				queryClient.invalidateQueries({ queryKey: SiteNotificationQueryKeys.notifications(companyHotel.id) });
				replaceCompanyHotelInCache(queryClient, data);
				toaster.success(`Enable Data Provider`, `The process to enable ${findProviderData(variables.provider).name} has been started. You will be informed when it is ready to be used`);
			},
		});

		const displayItem = (item: MenuButtonFilterItem<DataProvider>) => {
			return <div className="flex items-center justify-between gap-x-2 text-xs">
				<div className="flex items-center gap-x-2">
					{item.icon}
					<span className="font-semibold block">{item.name}</span>
				</div>
				{item.props?.canBeActivated &&
					<button type="button" className="font-semibold text-blue-600 hover:text-blue-500"
									onClick={() => enableCompanyHotelDataProvider.mutate({
										companyHotelId: companyHotel!.id,
										provider: item.value!,
									})}>• Activate</button>}
				{(item.props?.state === CompanyHotelParsingSettingState.PENDING || item.props?.state === CompanyHotelParsingSettingState.PROCESSING) &&
					<span
						className="inline-flex items-center gap-x-1 rounded-md bg-yellow-50 px-1.5 py-0.5 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
					<div><Spinner className="inline w-2.5 h-2.5 text-white fill-yellow-300 animate-spin bottom-[1px] relative" /></div>
					Pending
				</span>
				}
			</div>;
		};

		const createMenuItem = (provider: DataProvider) => {

			const parsingSettings = companyHotel.findParsingSettingsForProvider(provider);

			if(_isEmpty(parsingSettings)) {
				return {
					name: findProviderData(provider).name,
					icon: findProviderData(provider).icon,
					value: provider,
					disabled: true,
					props: {
						state: CompanyHotelParsingSettingState.INACTIVE,
						canBeActivated: false,
					},
				}
			}

			const canBeActivated = parsingSettings.every(v => v.state === CompanyHotelParsingSettingState.CAN_BE_ACTIVATED);
			let state = CompanyHotelParsingSettingState.INACTIVE;
			if (parsingSettings.some(v => v.state === CompanyHotelParsingSettingState.ACTIVE)) {
				state = CompanyHotelParsingSettingState.ACTIVE;
			} else if (parsingSettings.some(v => v.state === CompanyHotelParsingSettingState.PROCESSING)) {
				state = CompanyHotelParsingSettingState.PROCESSING;
			} else if (parsingSettings.some(v => v.state === CompanyHotelParsingSettingState.PENDING)) {
				state = CompanyHotelParsingSettingState.PENDING;
			} else if (canBeActivated) {
				state = CompanyHotelParsingSettingState.CAN_BE_ACTIVATED;
			}

			return {
				name: findProviderData(provider).name,
				icon: findProviderData(provider).icon,
				value: provider,
				disabled: state !== CompanyHotelParsingSettingState.ACTIVE,
				props: {
					state,
					canBeActivated,
				},
			};
		};

		const menuItems = [
			{
				name: 'Any Provider',
				description: 'Any Provider',
				value: undefined,
				icon: <div
					className="flex items-center rounded-sm bg-blue-100 px-0.5 py-0.5 text-blue-900 ring-1 ring-inset ring-blue-700/20">
					<BuildingOffice2Icon className="block h-4 w-4 text-gray-500" /></div>,
				props: {
					state: CompanyHotelParsingSettingState.ACTIVE,
					canBeActivated: false,
				},
			},
			createMenuItem(DataProvider.BOOKING),
			createMenuItem(DataProvider.EXPEDIA),
			createMenuItem(DataProvider.HOLIDAYCHECK),
			createMenuItem(DataProvider.BRAND_COM),
			createMenuItem(DataProvider.TRAVELSUPERMARKET),
			createMenuItem(DataProvider.TUIUK),
			createMenuItem(DataProvider.TUIDE),
			createMenuItem(DataProvider.JET2),
			createMenuItem(DataProvider.AIRBNB),
		];

		const onSelect = (value: DataProvider) => {
			onFilter({ ...filter, provider: value });
		};

		return <MenuButtonFilter menuItems={menuItems}
														 selectedValue={filter.provider}
														 onSelect={onSelect}
														 menuDescription="Please select the source you wish to compare."
														 displayItem={displayItem}
														 menuWidth="w-64" />;
	},
);

export default DashboardSelectProviderFilter;
